import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import SENTRY from 'config/sentry.singleton';
import ELID from 'constants/elid.const';
import USER_ROLE from 'constants/role';
import { useSalonBasicInfo } from 'providers/SalonBasicInfo.provider';
import selectEffectiveUserRole from 'selectors/selectEffectiveUserRole';
import { readCognitoStringAttribute, selectCognitoAttributes, selectCognitoAttributeSub } from 'utils/cognito.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import isFn from 'utils/predicates/function/isFn.util';

const IntercomWatcher = () => {
  // userId must be the Cognito sub (UUID) to match SEGMENT.identify in trackCognitoLogin
  // and existing legacy Intercom contacts whose external_id is the sub.
  const sub = useSelector(selectCognitoAttributeSub);
  const attributes = useSelector(selectCognitoAttributes);
  const userId = fromAnyToString(sub);
  const userEmail = readCognitoStringAttribute(attributes, 'email');
  const userName = readCognitoStringAttribute(attributes, 'name');
  const userPhone = readCognitoStringAttribute(attributes, 'phone_number');
  // Only forward 'manager'/'receptionist' - 'no_access'/'' would overwrite a good value.
  const userRole = useSelector(selectEffectiveUserRole);
  const userType = userRole === USER_ROLE.manager || userRole === USER_ROLE.receptionist ? userRole : '';
  const { salonId, salonName } = useSalonBasicInfo();

  const lastAttachment = useRef(null);

  // Belt-and-braces re-identify for refreshed sessions where COGNITO_LOGIN already fired.
  useEffect(() => {
    if (!userId) return;
    if (!isFn(window?.analytics?.identify)) return;

    const traits = {
      ...(userName && { name: userName }),
      ...(userEmail && { email: userEmail }),
      ...(userPhone && { phone: userPhone }),
      // Key must match the BE overnight job: user.custom_attributes['User type'] in api.core salons_manager/tasks.py.
      ...(userType && { 'User type': userType }),
    };
    if (!traits.name && !traits.email && !traits.phone && !traits['User type']) return;

    window.analytics.identify(userId, traits, { integrations: { All: false, Intercom: true } });
  }, [userId, userEmail, userName, userPhone, userType]);

  useEffect(() => {
    if (!userId || !salonId || !salonName) return;
    if (!isFn(window?.analytics?.group)) return;

    const alreadyAttached = lastAttachment.current;

    if (alreadyAttached && alreadyAttached.userId === userId && alreadyAttached.salonId === salonId && alreadyAttached.name === salonName) {
      return;
    }

    if (alreadyAttached) {
      SENTRY.error({
        logger: 'IntercomWatcher',
        tags: { component: 'IntercomWatcher' },
        extra: {
          previousUserId: alreadyAttached.userId,
          previousSalonId: alreadyAttached.salonId,
          previousSalonName: alreadyAttached.name,
          currentUserId: userId,
          currentSalonId: salonId,
          currentSalonName: salonName,
          currentUrl: window.location.href,
          timestamp: new Date().toISOString(),
        },
        error: new Error(`IntercomWatcher: Re-attaching user`),
      });
    }

    window.analytics.group(salonId, { name: salonName }, { userId });

    lastAttachment.current = { userId, salonId, name: salonName };
  }, [userId, salonId, salonName]);

  return <div data-bem="IntercomWatcher" id={ELID.intercomwatch} />;
};

export default IntercomWatcher;
